import React, { FC } from "react";
import { useParams } from "react-router-dom";
import MenuItem from "@mui/material/MenuItem";
import type { MenuItemProps } from "@mui/material/MenuItem";
import {
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarExportContainer,
  GridPrintExportMenuItem,
  gridDataRowIdsSelector,
  useGridApiContext,
} from "../../Unknown/DataGrid";
import DateRangePicker, {
  DateRangePickerProps,
} from "../../Unknown/DateRangePicker";
import Box from "../../Unknown/Box";
import useStyles from "./useStyles";
import useTranslations from "./useTranslations";
import type { AppointmentRowItem } from "../AppointmentTableGrid/types";

interface AppointmentTableTopBarProps {
  dateRangePickerProps?: DateRangePickerProps<Date>;
  fetchAllRowsForExport?: () => Promise<AppointmentRowItem[]>;
}

async function exportAllRows(
  apiRef: ReturnType<typeof useGridApiContext>,
  fetchAllRowsForExport: () => Promise<AppointmentRowItem[]>,
  exportFn: () => void | Promise<void>,
) {
  const currentRowIds = gridDataRowIdsSelector(apiRef.current.state);
  const allRows = await fetchAllRowsForExport();
  apiRef.current.updateRows(allRows);
  await exportFn();
  const idsToDelete = allRows
    .map((row) => row.id)
    .filter((id) => !currentRowIds.includes(id));
  apiRef.current.updateRows(
    idsToDelete.map((id) => ({ id, _action: "delete" })),
  );
}

type ExportMenuItemProps = MenuItemProps & {
  hideMenu?: () => void;
  fetchAllRowsForExport: () => Promise<AppointmentRowItem[]>;
};

const CsvExportMenuItem: FC<ExportMenuItemProps> = ({
  hideMenu,
  fetchAllRowsForExport,
  ...rest
}) => {
  const apiRef = useGridApiContext();
  return (
    <MenuItem
      {...rest}
      onClick={() => {
        hideMenu?.();
        exportAllRows(apiRef, fetchAllRowsForExport, () =>
          apiRef.current.exportDataAsCsv({
            fileName: "appointments-export",
            includeHeaders: true,
          }),
        );
      }}
    >
      {apiRef.current.getLocaleText("toolbarExportCSV")}
    </MenuItem>
  );
};

const AppointmentTableTopBar: FC<AppointmentTableTopBarProps> = ({
  dateRangePickerProps,
  fetchAllRowsForExport,
}) => {
  const { role } = useParams();
  const {
    toolbarRoot,
    datePickerTextFieldRoot,
    datePickerTextFieldInput,
    datePickerFieldSeparatorRoot,
  } = useStyles();
  const { fromDateLabel, toDateLabel } = useTranslations();

  switch (role) {
    case "regional_admin":
    case "super_admin": {
      return (
        <Box
          display="flex"
          justifyContent="space-between"
          className={toolbarRoot}
        >
          <Box p={2} display="flex" justifyContent="flex-start">
            <GridToolbarColumnsButton color="primary" variant="text" />
            <GridToolbarFilterButton />
            {fetchAllRowsForExport ? (
              <GridToolbarExportContainer>
                <CsvExportMenuItem
                  fetchAllRowsForExport={fetchAllRowsForExport}
                />
                <GridPrintExportMenuItem />
              </GridToolbarExportContainer>
            ) : (
              <GridToolbarExport />
            )}
          </Box>
          <Box
            className={toolbarRoot}
            p={2}
            display="flex"
            justifyContent="flex-end"
          >
            {dateRangePickerProps && (
              <DateRangePicker
                slotProps={{
                  textField: {
                    size: "small",
                    classes: {
                      root: datePickerTextFieldRoot,
                    },
                    InputProps: {
                      classes: {
                        input: datePickerTextFieldInput,
                      },
                    },
                  },
                  fieldSeparator: {
                    classes: {
                      root: datePickerFieldSeparatorRoot,
                    },
                  },
                }}
                localeText={{
                  start: fromDateLabel,
                  end: toDateLabel,
                }}
                {...dateRangePickerProps}
              />
            )}
          </Box>
        </Box>
      );
    }
    default: {
      return (
        <Box
          className={toolbarRoot}
          p={2}
          display="flex"
          justifyContent="flex-start"
        >
          <GridToolbarFilterButton />
        </Box>
      );
    }
  }
};

export default AppointmentTableTopBar;
